"use client";

import { cn } from "@/shared/libs/commonUtils";
import { formatCount } from "@/shared/libs/componentUtils";
import { type InputHTMLAttributes, useEffect, useState } from "react";
import { Button } from "../button";
import Flex from "../Flex";
import Typography from "../Typography";
import type {
  TCounterButtonPlaceholder,
  TCounterClassName,
  TCounterDisable,
  TFormatOptions,
} from "./counter.types";

interface CounterProps extends InputHTMLAttributes<HTMLInputElement> {
  orientation?: "vertical" | "horizontal";
  limitMessage?: string;
  format?: ((count: number) => string) | TFormatOptions;
  limit?: number;
  minLimit?: number;
  initial?: number;
  wrap?: boolean;
  step?: number;
  buttonPlaceHolders?: TCounterButtonPlaceholder;
  disable?: TCounterDisable;
  classNames?: TCounterClassName;
  onCount?: (count: number) => void;
  name?: string;
  total?: number;
  totalLimit?: number;
}

export default function Counter({
  orientation,
  limitMessage,
  limit,
  minLimit,
  initial = 1,
  wrap,
  step = 1,
  buttonPlaceHolders,
  disable,
  format,
  classNames,
  name,
  total,
  totalLimit,
  onCount,
  ...props
}: CounterProps) {
  const [count, setCount] = useState(initial);
  const { icons, text } = buttonPlaceHolders ?? {};

  const handleCount = (type: "increase" | "decrease") => () => {
    switch (type) {
      case "increase":
        {
          if (total && totalLimit) {
            if (total < totalLimit) {
              if (limit) {
                if (count < limit) {
                  setCount((pre) => pre + step);
                  !!onCount && onCount(count + step);
                }
              } else {
                setCount((pre) => pre + step);
                !!onCount && onCount(count + step);
              }

              if (count === limit && wrap) setCount(0);
            }
          } else if (limit) {
            if (count < limit) {
              setCount((pre) => pre + step);
              !!onCount && onCount(count + step);
            }
          } else {
            setCount((pre) => pre + step);
            !!onCount && onCount(count + step);
          }

          if (count === limit && wrap) setCount(0);
        }
        break;

      case "decrease": {
        if (minLimit) {
          if (count !== 0 && count - step >= minLimit) {
            setCount((pre) => pre - step);
            !!onCount && onCount(count - step);
          }
        } else if (count !== 0 && count - step >= 0) {
          setCount((pre) => pre - step);
          !!onCount && onCount(count - step);
        }

        if (limit && count === 0 && wrap) {
          setCount(limit);
        }
      }
    }
  };

  useEffect(() => {
    if (limit && count > limit) {
      setCount(limit);
    }
  }, [count, limit]);

  return (
    <Flex
      direction={orientation === "vertical" ? "col" : "row"}
      wrap={"nowrap"}
      gap={"6"}
      alignItems={"center"}
      className={cn("relative", classNames?.parent)}
      {...props}
    >
      {/* Increase button */}
      <Button
        size="icon"
        onClick={handleCount("increase")}
        className={cn(
          "bg-transparent! shadow-none text-primary",
          classNames?.increase
        )}
        disabled={disable?.increase ?? false}
        aria-label="Increase count"
      >
        {icons?.increase || text?.increase}
      </Button>

      {/* Count */}
      <Flex.Item
        className={cn("justify-center text-nowrap", classNames?.counter)}
      >
        <Typography className={classNames?.counterText}>
          {format ? formatCount(count, format) : count}
        </Typography>
      </Flex.Item>

      {/* Limit message */}
      <Flex.Item
        className={cn(
          "absolute bottom-1/2 left-1/2 -translate-x-1/2 translate-y-[220%]",
          limitMessage ? "visible" : "invisible"
        )}
      >
        <Typography
          variant="body2"
          className={cn("text-destructive", classNames?.limitMessage)}
        >
          {limitMessage}
        </Typography>
      </Flex.Item>

      {/* Decrease button */}
      <Button
        size="icon"
        variant={"default"}
        onClick={handleCount("decrease")}
        className={cn(
          "bg-transparent! shadow-none text-primary",
          classNames?.decrease
        )}
        disabled={disable?.decrease ?? false}
        aria-label="Decrease count"
      >
        {icons?.decrease || text?.decrease}
      </Button>
    </Flex>
  );
}
